import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { i18next, site } from '@yola/ws-sdk';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import useOnceWhen from 'src/js/modules/utils/custom-hooks/use-once-when';
import fieldNames from '../constants/field-names';
import createAnchorValidator from '../validators/create-anchor-validator';

const { SearchableSelectField } = designSystem;
const { useWatch } = reactHookForm;

const findOptionByValue = (value, options) => {
  const isGroupOptions = Boolean(options[0]?.options);

  if (isGroupOptions) {
    const allOptions = options.reduce(
      (resultArray, currentOption) => resultArray.concat(currentOption.options),
      []
    );

    return allOptions.find((option) => option.value === value);
  }

  return options.find((option) => option.value === value);
};

const getPageIdFromOption = (value, options, defaultPageId) => {
  const selectedOption = findOptionByValue(value, options);

  return selectedOption?.pageId || defaultPageId;
};

function AnchorField({
  control,
  options,
  menuPlacement = 'auto',
  getFormValues,
  setFormValue,
  onChange,
}) {
  const activePageId = useSelector(site.selectors.getActivePageId);
  const anchorReference = useWatch({ control, name: fieldNames.ANCHOR_REFERENCE });

  const hasInvalidReference =
    Boolean(anchorReference) && !findOptionByValue(anchorReference, options);

  const handlePageIdChange = useCallback(
    (value) => {
      if (!value) {
        return;
      }

      const pageId = getPageIdFromOption(value, options, activePageId);
      const oldPageId = getFormValues(fieldNames.ANCHOR_PAGE_ID);

      if (pageId !== oldPageId) {
        setFormValue(fieldNames.ANCHOR_PAGE_ID, pageId);
        onChange({
          fieldName: fieldNames.ANCHOR_PAGE_ID,
          fieldValue: pageId,
        });
      }
    },
    [options, activePageId, onChange, getFormValues, setFormValue]
  );

  const handleChange = useCallback(
    (value) => {
      onChange({
        fieldName: fieldNames.ANCHOR_REFERENCE,
        fieldValue: value,
      });
      handlePageIdChange(value);
    },
    [onChange, handlePageIdChange]
  );

  const clearInvalidReference = useCallback(() => {
    setFormValue(fieldNames.ANCHOR_REFERENCE, '', { shouldDirty: true });
  }, [setFormValue]);

  useOnceWhen(hasInvalidReference, clearInvalidReference);

  return (
    <SearchableSelectField
      name={fieldNames.ANCHOR_REFERENCE}
      control={control}
      rules={{ validate: createAnchorValidator() }}
      options={options}
      placeholder={i18next.t('Select block...')}
      noResultsText={i18next.t('No results found')}
      menuPlacement={menuPlacement}
      onChange={handleChange}
    />
  );
}

AnchorField.propTypes = {
  control: PropTypes.shape({}).isRequired,
  options: PropTypes.array.isRequired,
  menuPlacement: PropTypes.string,
  getFormValues: PropTypes.func.isRequired,
  setFormValue: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default AnchorField;
